import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import {
  getListJalurFeeder,
  getListJenisDaftarFeeder,
  getListLayananPlaza,
  getListSubLayanan2Plaza,
} from "@/services/getData";
import {
  Jalur,
  JalurFeeder,
  JalurGroup,
  JalurJenis,
  JenisDaftarFeeder,
  LayananPlaza,
  SubLayanan2Plaza,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdModeEdit } from "react-icons/md";
import { RiDeleteBack2Line } from "react-icons/ri";

const postData = async (newData: JalurJenis) => {
  const response = await api.post("/master/jalur-jenis/store", {
    ...newData,
    id_feeder: parseInt(newData.id_feeder),
    id_ref_jalur: parseInt(newData.id_ref_jalur),
    id_ref_jalur_grup: parseInt(newData.id_ref_jalur_grup),
    id_layanan_plaza_daftar: parseInt(newData.id_layanan_plaza_daftar),
    id_feeder_jenis_daftar: parseInt(newData.id_feeder_jenis_daftar),
  });
  return response.data;
};

const patchData = async (newData: JalurJenis) => {
  const response = await api.patch("/master/jalur-jenis/update", {
    ...newData,
    id_feeder: parseInt(newData.id_feeder),
    id_ref_jalur: parseInt(newData.id_ref_jalur),
    id_ref_jalur_grup: parseInt(newData.id_ref_jalur_grup),
    id_layanan_plaza_daftar: parseInt(newData.id_layanan_plaza_daftar),
    id_feeder_jenis_daftar: parseInt(newData.id_feeder_jenis_daftar),
  });
  return response.data;
};

const deleteData = async (deletedData: { id_ref_jalur_jenis: string }) => {
  const response = await api({
    url: "/master/jalur-jenis/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

const getListJalur = async () => {
  const response = await api.get("/master/jalur/index", {
    withCredentials: true,
  });
  return response.data?.data;
};

const getListJalurGroup = async () => {
  const response = await api.get("/master/jalur-grup/index", {
    withCredentials: true,
  });
  return response.data?.data;
};

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: JalurJenis | null;
  closeModal: () => void;
}) {
  const { data: dataJalur } = useQuery({
    queryKey: ["listJalur"],
    queryFn: getListJalur,
  });
  const { data: dataJalurGroup } = useQuery({
    queryKey: ["listJalurGroup"],
    queryFn: getListJalurGroup,
  });
  const { data: dataJalurFeeder } = useQuery({
    queryKey: ["listJalurFeeder"],
    queryFn: getListJalurFeeder,
  });
  const { data: dataJenisDaftarFeeder } = useQuery({
    queryKey: ["listJenisDaftarFeeder"],
    queryFn: getListJenisDaftarFeeder,
  });

  const { data: listLayananPlaza, isPending: isPendingListLayananPlaza } =
    useQuery({
      queryKey: ["listLayananPlaza"],
      queryFn: getListLayananPlaza,
      select: (data) =>
        data?.map((layanan: LayananPlaza) => {
          return {
            label: layanan.nama_layanan,
            value: `${layanan.layanan_id}`,
          };
        }),
    });

  const {
    data: listSubLayanan2Plaza,
    isPending: isPendingListSubLayanan2Plaza,
  } = useQuery({
    queryKey: ["listSubLayanan2Plaza"],
    queryFn: getListSubLayanan2Plaza,
    select: (data) =>
      data?.map((sublayanan: SubLayanan2Plaza) => {
        return {
          label: sublayanan.kode_sublayanan2,
          value: sublayanan.kode_sublayanan2,
        };
      }),
  });

  const [openLayananPlazaDaftar, setOpenLayananPlazaDaftar] = useState(false);
  const [openLayananPlazaUkt, setOpenLayananPlazaUkt] = useState(false);
  const [openSubLayanan2Plaza, setOpenSubLayanan2Plaza] = useState(false);

  const [form, setForm] = useState<JalurJenis>({
    id_ref_jalur_jenis: selectedRow?.id_ref_jalur_jenis || "",
    // kode_jalur: selectedRow?.kode_jalur || "",
    id_ref_jalur_grup: selectedRow?.id_ref_jalur_grup || "",
    nama_jalur_jenis_slug: selectedRow?.nama_jalur_jenis_slug || "",
    nama_jalur_jenis: selectedRow?.nama_jalur_jenis || "",
    id_ref_jalur: selectedRow?.id_ref_jalur || "",
    id_feeder: selectedRow?.id_feeder || "",
    id_feeder_jenis_daftar: selectedRow?.id_feeder_jenis_daftar || "",
    is_cek_pusat: selectedRow?.is_cek_pusat ?? false,
    prefix_nomor_pendaftaran: selectedRow?.prefix_nomor_pendaftaran || "",
    suffix_nomor_pendaftaran: selectedRow?.suffix_nomor_pendaftaran || "",
    is_laporan_snpmb: selectedRow?.is_laporan_snpmb ?? false,
    is_cbt_internal: selectedRow?.is_cbt_internal ?? false,
    is_tampil: selectedRow?.is_tampil ?? true,
    urutan_tampil: selectedRow?.urutan_tampil || "",
    deskripsi: selectedRow?.deskripsi || "",
    id_layanan_plaza_daftar: selectedRow?.id_layanan_plaza_daftar || "",
    id_layanan_plaza_ukt: selectedRow?.id_layanan_plaza_ukt || "",
    kode_sublayanan2_plaza: selectedRow?.kode_sublayanan2_plaza || "",
  });

  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJalurJenis"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jalur Jenis"
          description={`Apakah anda yakin akan menghapus data jalur "${selectedRow?.nama_jalur_jenis}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Jalur Jenis" : "Update Data Jalur Jenis"}
      />
      <section className="grid grid-cols-2 gap-3">
        <div>
          <Label>Ref Jalur</Label>
          <Select
            value={form.id_ref_jalur}
            onValueChange={(value) => {
              if (!value) return;
              setForm((prev) => ({ ...prev, id_ref_jalur: value }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Ref Jalur"}>
                {form.id_ref_jalur &&
                  dataJalur?.find(
                    (jalur: Jalur) => jalur.id_ref_jalur === form.id_ref_jalur,
                  )?.nama_jalur}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {dataJalur?.map((jalur: Jalur) => (
                <SelectItem
                  key={jalur.id_ref_jalur}
                  value={`${jalur.id_ref_jalur}`}
                >
                  {jalur.nama_jalur}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Ref Jalur Group</Label>
          <Select
            value={form.id_ref_jalur_grup}
            onValueChange={(value) => {
              if (!value) return;
              setForm((prev) => ({ ...prev, id_ref_jalur_grup: value }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Ref Jalur"}>
                {form.id_ref_jalur_grup &&
                  dataJalurGroup?.find(
                    (jalur: JalurGroup) =>
                      jalur.id_ref_jalur_grup == form.id_ref_jalur_grup,
                  )?.nama_jalur}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {dataJalurGroup?.map((jalur: JalurGroup) => (
                <SelectItem
                  key={jalur.id_ref_jalur_grup}
                  value={`${jalur.id_ref_jalur_grup}`}
                >
                  {jalur.nama_jalur_grup}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Nama Jalur Jenis</Label>
          <Input
            placeholder="Ketik nama jalur jenis..."
            value={form.nama_jalur_jenis}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                nama_jalur_jenis: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Slug</Label>
          <Input
            placeholder="Ketik slug..."
            value={form.nama_jalur_jenis_slug}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                nama_jalur_jenis_slug: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label>Jalur Feeder</Label>
          <Select
            value={form.id_feeder}
            onValueChange={(value) => {
              if (!value) return;
              setForm((prev) => ({ ...prev, id_feeder: value }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Jalur Feeder"}>
                {form.id_feeder &&
                  dataJalurFeeder?.find(
                    (jalurFeeder: JalurFeeder) =>
                      jalurFeeder.id_feeder === form.id_feeder,
                  )?.nama_jalur_masuk}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {dataJalurFeeder?.map((jalurFeeder: JalurFeeder) => (
                <SelectItem
                  key={jalurFeeder.id_feeder}
                  value={`${jalurFeeder.id_feeder}`}
                >
                  {jalurFeeder.nama_jalur_masuk}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Jenis Daftar Feeder</Label>
          <Select
            value={form.id_feeder_jenis_daftar}
            onValueChange={(value) => {
              if (!value) return;
              setForm((prev) => ({
                ...prev,
                id_feeder_jenis_daftar: value,
              }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Jenis Daftar"}>
                {form.id_feeder_jenis_daftar &&
                  dataJenisDaftarFeeder?.find(
                    (jenisDaftar: JenisDaftarFeeder) =>
                      jenisDaftar.id_ref_jenis_daftar_feeder ==
                      form.id_feeder_jenis_daftar,
                  )?.nama_jenis_daftar}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {dataJenisDaftarFeeder?.map((jenisDaftar: JenisDaftarFeeder) => (
                <SelectItem
                  key={jenisDaftar.id_ref_jenis_daftar_feeder}
                  value={`${jenisDaftar.id_ref_jenis_daftar_feeder}`}
                >
                  {jenisDaftar.nama_jenis_daftar}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Prefix Nomor Pendaftaran</Label>
          <Input
            required
            placeholder="Ketik prefix nomor pendaftaran..."
            value={form.prefix_nomor_pendaftaran}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                prefix_nomor_pendaftaran: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label>Suffix Nomor Pendaftaran</Label>
          <Input
            required
            placeholder="Ketik suffix nomor pendaftaran..."
            value={form.suffix_nomor_pendaftaran}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                suffix_nomor_pendaftaran: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label>Layanan Plaza Daftar</Label>
          <Combobox
            isPending={isPendingListLayananPlaza}
            open={openLayananPlazaDaftar}
            setOpen={setOpenLayananPlazaDaftar}
            value={form.id_layanan_plaza_daftar || ""}
            setValue={(value) => {
              setForm((prev) => ({ ...prev, id_layanan_plaza_daftar: value }));
            }}
            options={listLayananPlaza}
            placeholder="Pilih Layanan Plaza Daftar"
          />
        </div>
        <div>
          <Label>Layanan Plaza UKT</Label>
          <Combobox
            isPending={isPendingListLayananPlaza}
            open={openLayananPlazaUkt}
            setOpen={setOpenLayananPlazaUkt}
            value={form.id_layanan_plaza_ukt || ""}
            setValue={(value) => {
              setForm((prev) => ({ ...prev, id_layanan_plaza_ukt: value }));
            }}
            options={listLayananPlaza}
            placeholder="Pilih Layanan Plaza UKT"
          />
        </div>

        <div>
          <Label>Kode Sub Layanan 2 Plaza</Label>
          <Combobox
            isPending={isPendingListSubLayanan2Plaza}
            open={openSubLayanan2Plaza}
            setOpen={setOpenSubLayanan2Plaza}
            value={form.kode_sublayanan2_plaza || ""}
            setValue={(value) => {
              setForm((prev) => ({ ...prev, kode_sublayanan2_plaza: value }));
            }}
            options={listSubLayanan2Plaza}
            placeholder="Pilih Sub Layanan 2 Plaza"
          />
        </div>

        <div className="flex gap-3">
          <Switch
            id="is_cek_pusat"
            checked={form.is_cek_pusat}
            onCheckedChange={() =>
              setForm((prev) => ({
                ...prev,
                is_cek_pusat: !prev.is_cek_pusat,
              }))
            }
          />
          <Label htmlFor="is_cek_pusat">Berisi Cek Pusat (tidak/ya)</Label>
        </div>

        <div className="flex gap-3">
          <Switch
            id="is_laporan_snpmb"
            checked={form.is_laporan_snpmb}
            onCheckedChange={() =>
              setForm((prev) => ({
                ...prev,
                is_laporan_snpmb: !prev.is_laporan_snpmb,
              }))
            }
          />
          <Label htmlFor="is_laporan_snpmb">Laporan SNPMB (tidak/ya)</Label>
        </div>

        <div className="flex gap-3">
          <Switch
            id="is_cbt_internal"
            checked={form.is_cbt_internal}
            onCheckedChange={() =>
              setForm((prev) => ({
                ...prev,
                is_cbt_internal: !prev.is_cbt_internal,
              }))
            }
          />
          <Label htmlFor="is_cbt_internal">CBT Internal (tidak/ya)</Label>
        </div>

        <div className="flex gap-3">
          <Switch
            id="is_tampil"
            checked={form.is_tampil}
            onCheckedChange={() =>
              setForm((prev) => ({
                ...prev,
                is_tampil: !prev.is_tampil,
              }))
            }
          />
          <Label htmlFor="is_tampil">Status Tampil (tidak/ya)</Label>
        </div>

        <div>
          <Label>Urutan Tampil</Label>
          <Input
            placeholder="Ketik urutan tampil di dashboard siswa..."
            value={form.urutan_tampil}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                urutan_tampil: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Deskripsi</Label>
          <Textarea
            placeholder="Ketik deskripsi..."
            className="mt-1"
            value={form.deskripsi}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                deskripsi: event.target.value,
              }))
            }
          />
        </div>
      </section>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  );
}
